import { useEffect, useRef } from "react";
import {
  CandlestickSeries,
  ColorType,
  createChart,
  HistogramSeries,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import { CANDLE_INTERVALS, type CandleDto, type CandleInterval } from "../shared/contracts.ts";
import { numberValue } from "./format.ts";

const INTERVAL_LABELS: Record<CandleInterval, string> = {
  "1m": "1м",
  "5m": "5м",
  "10m": "10м",
  "1h": "1ч",
  "4h": "4ч",
  "1d": "1д",
  "1w": "1н",
  "1mo": "1мес",
};

function timestamp(value: string): UTCTimestamp {
  return Math.floor(new Date(value).getTime() / 1_000) as UTCTimestamp;
}

function candleData(candle: CandleDto) {
  return {
    time: timestamp(candle.time),
    open: numberValue(candle.open) ?? 0,
    high: numberValue(candle.high) ?? 0,
    low: numberValue(candle.low) ?? 0,
    close: numberValue(candle.close) ?? 0,
  };
}

function volumeData(candle: CandleDto) {
  return {
    time: timestamp(candle.time),
    value: numberValue(candle.volume) ?? 0,
    color: (numberValue(candle.close) ?? 0) >= (numberValue(candle.open) ?? 0)
      ? "rgba(44, 232, 156, .38)"
      : "rgba(255, 117, 132, .38)",
  };
}

interface ChartWidgetProps {
  candles: CandleDto[];
  instrumentUid?: string;
  interval: CandleInterval;
  onIntervalChange: (interval: CandleInterval) => void;
  ticker?: string;
}

export function ChartWidget({
  candles,
  instrumentUid,
  interval,
  onIntervalChange,
  ticker,
}: ChartWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const dataKeyRef = useRef("");
  const dataLengthRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      autoSize: true,
      layout: {
        attributionLogo: false,
        background: { type: ColorType.Solid, color: "#1e2c39" },
        panes: { enableResize: false, separatorColor: "#3c5469" },
        textColor: "#8ca7be",
      },
      grid: {
        horzLines: { color: "rgba(60, 84, 105, .42)" },
        vertLines: { color: "rgba(60, 84, 105, .32)" },
      },
      rightPriceScale: { borderColor: "#3c5469" },
      timeScale: { borderColor: "#3c5469", timeVisible: true, secondsVisible: false },
      localization: { locale: "ru-RU" },
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      borderDownColor: "#ff7584",
      borderUpColor: "#2ce89c",
      downColor: "#ff7584",
      upColor: "#2ce89c",
      wickDownColor: "#ff7584",
      wickUpColor: "#2ce89c",
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceLineVisible: false,
      lastValueVisible: false,
    }, 1);
    chart.panes()[0]?.setStretchFactor(4);
    chart.panes()[1]?.setStretchFactor(1);

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;
    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!candleSeries || !volumeSeries) return;
    const key = `${instrumentUid ?? "none"}:${interval}`;
    const keyChanged = dataKeyRef.current !== key;
    const fullRefresh = keyChanged || dataLengthRef.current === 0 || candles.length < dataLengthRef.current;
    if (fullRefresh) {
      candleSeries.setData(candles.map(candleData));
      volumeSeries.setData(candles.map(volumeData));
      if (candles.length > 0) chartRef.current?.timeScale().fitContent();
    } else {
      const last = candles.at(-1);
      if (last) {
        candleSeries.update(candleData(last));
        volumeSeries.update(volumeData(last));
      }
    }
    dataKeyRef.current = key;
    dataLengthRef.current = candles.length;
  }, [candles, instrumentUid, interval]);

  return (
    <section className="widget chart-widget" data-widget="chart">
      <header className="widget-header chart-header">
        <div>
          <span className="widget-title">График</span>
          <strong className="chart-ticker">{ticker ?? "—"}</strong>
        </div>
        <div className="timeframes" aria-label="Таймфрейм">
          {CANDLE_INTERVALS.map((value) => (
            <button
              aria-pressed={value === interval}
              className={value === interval ? "active" : ""}
              key={value}
              onClick={() => onIntervalChange(value)}
              type="button"
            >
              {INTERVAL_LABELS[value]}
            </button>
          ))}
        </div>
      </header>
      <div className="chart-canvas" data-testid="chart-canvas" ref={containerRef} />
    </section>
  );
}
